import { useState, useCallback } from 'react';
import { useList, useCustom, useApiUrl, useNavigation } from '@refinedev/core';
import { formatDate } from '@/lib/utils';
import { SortableHeader, type SortOrder } from '@/components/table/SortableHeader';
import {
  ImageIcon,
  Shield,
  CheckCircle,
  XCircle,
  ArrowLeft,
  Loader2,
  Clock,
  ExternalLink,
  Eye,
  Scale,
  X,
} from 'lucide-react';
import { getIdToken } from '@/providers/auth-provider';
import { LegalHoldsTab } from './LegalHolds';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import { ModerationCheckCard, type ModerationCheck } from './ModerationCheckCard';
import { RefusedPhotoReview } from './RefusedPhotoReview';
import type { QuarantineItem } from './refused-photos-model';
import {
  FlagChips, MemberRefusalsChip, QueueThumb, StatusBadge, StatusLegend, moderationStatus,
} from './moderation-list-parts';
import {
  CONFIDENCE_CHECK_TYPE, PHOTO_SUBJECT_CATEGORIES, PHOTO_SUBJECT_CATEGORY_TEXT, splitFlaggedFor,
} from './photo-subject-categories';

/**
 * Photo thumbnail with graceful load-failure handling.
 *
 * Why this exists (2026-05-26 audit): the moderation pipeline auto-deletes
 * the S3 object on `result='fail'` for safety, while the
 * `core.photo_moderation_log` row persists for audit. The backend's
 * `signPhotoUrl` happily signs a URL against the (now-missing) S3 key,
 * which the browser then fetches and gets a 404/403 — rendering a
 * broken-image icon. The old code branched on `url ? <img/> : <placeholder/>`
 * which only handled the null case; non-null-but-deleted URLs slipped through.
 *
 * This component handles both: it tries to render the image, and on `onError`
 * swaps to the placeholder. Communicates to the matchmaker that the photo
 * was removed (not that the admin tool is broken).
 */
function PhotoThumb({
  src,
  alt = '',
  className = 'h-12 w-12 rounded-lg object-cover',
  placeholderClassName = 'flex h-12 w-12 items-center justify-center rounded-lg bg-muted',
  placeholderIconClassName = 'h-5 w-5 text-muted-foreground',
  removedLabel,
}: {
  src: string | null;
  alt?: string;
  className?: string;
  placeholderClassName?: string;
  placeholderIconClassName?: string;
  removedLabel?: string;
}) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className={placeholderClassName} title={!src ? 'Photo unavailable' : 'Photo removed (failed auto-moderation)'}>
        <ImageIcon className={placeholderIconClassName} />
        {removedLabel && (
          <span className="ml-2 text-xs text-muted-foreground">{removedLabel}</span>
        )}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setFailed(true)}
    />
  );
}

/** Why "Min confidence" is off: only Rekognition scores a photo (issue #3608). */
const CONFIDENCE_ONLY_EXPLICIT =
  'Only the explicit-content check records a confidence. Choose "Explicit content" to use this filter.';

interface QueueItem {
  photo_id: string;
  user_id: string;
  s3_key: string;
  email: string;
  display_name: string | null;
  thumbnail_url: string | null;
  /** A refused explicit photo: its server-rendered blur (a data: URL), never a file URL. */
  blurred_thumbnail: string | null;
  /** Set when the photo is in the refused-photo review: its decision there. */
  quarantine: { decision: string; blurred: boolean; file_kept: boolean } | null;
  /** How many refused photos of the same member wait for review. */
  member_waiting: number;
  admin_decision: string | null;
  created_at: string;
  checks: ModerationCheck[];
}

interface ModerationDetail {
  photo_id: string;
  user_id: string;
  s3_key: string;
  email: string;
  display_name: string | null;
  photo_url: string | null;
  moderation_status: string | null;
  visibility: string | null;
  is_primary: boolean | null;
  checks: (ModerationCheck & {
    reviewed_by_admin: string | null;
    admin_decision: string | null;
    created_at: string;
  })[];
  user_photos: {
    photo_id: string;
    url: string | null;
    is_primary: boolean;
    visibility: string;
    moderation_status: string;
  }[];
  /** Set when the photo is in the refused-photo review: it is decided ON THIS PAGE (2026-10-19). */
  quarantine: QuarantineItem | null;
}

interface ModerationStats {
  pending_count: number;
  rejected_count: number;
  overridden_count: number;
  flagged_today: number;
  flagged_this_week: number;
}

function ReviewDetail({
  photoId,
  onBack,
  onResolved,
}: {
  photoId: string;
  onBack: () => void;
  onResolved: () => void;
}) {
  const apiUrl = useApiUrl();
  const { show } = useNavigation();
  const [resolving, setResolving] = useState(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const { data: detailData, isLoading, refetch } = useCustom<ModerationDetail>({
    url: `/v1/admin/moderation/photos/${photoId}`,
    method: 'get',
  });

  const detail = detailData?.data ?? null;

  const handleResolve = useCallback(async (decision: 'approve' | 'reject') => {
    setResolving(true);
    setError(null);
    try {
      const token = await getIdToken();
      const res = await fetch(`${apiUrl}/v1/admin/moderation/photos/${photoId}/review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ decision, notes: notes || undefined }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onResolved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resolve');
    } finally {
      setResolving(false);
    }
  }, [apiUrl, photoId, notes, onResolved]);

  return (
    <div className="space-y-6">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Queue
      </button>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : detail ? (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Photo Review</h2>
              <p className="text-muted-foreground">{detail.email}</p>
            </div>
            <button
              onClick={() => show('users', detail.user_id)}
              className="flex items-center gap-2 text-sm text-primary hover:underline"
            >
              View User Profile <ExternalLink className="h-3 w-3" />
            </button>
          </div>

          {error && (
            <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-destructive text-sm">
              {error}
            </div>
          )}
          {notice && (
            <div role="status" className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">{notice}</div>
          )}

          {/* Photo + User Info */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Flagged Photo: a refused photo is decided here, in place (2026-10-19) */}
            <div className="rounded-lg border bg-card p-4">
              <div className="text-sm font-medium text-muted-foreground mb-3">
                {detail.quarantine ? 'Refused photo — decide it here' : 'Flagged Photo'}
              </div>
              {detail.quarantine ? (
                <RefusedPhotoReview
                  item={detail.quarantine}
                  onOpenUser={(userId) => show('users', userId)}
                  onDecided={(message) => {
                    setNotice(message);
                    void refetch();
                  }}
                />
              ) : (
                <PhotoThumb
                  src={detail.photo_url}
                  alt="Flagged photo"
                  className="w-full max-h-96 object-contain rounded-lg bg-muted"
                  placeholderClassName="flex h-48 items-center justify-center gap-2 rounded-lg bg-muted text-muted-foreground"
                  placeholderIconClassName="h-12 w-12"
                  removedLabel="Photo file unavailable"
                />
              )}
            </div>

            {/* User's Other Photos */}
            <div className="rounded-lg border bg-card p-4">
              <div className="text-sm font-medium text-muted-foreground mb-3">
                All Photos by User ({detail.user_photos.length})
              </div>
              <div className="grid grid-cols-3 gap-2">
                {detail.user_photos.map((p) => (
                  <div
                    key={p.photo_id}
                    className={`relative rounded-lg overflow-hidden ${p.photo_id === detail.photo_id ? 'ring-2 ring-primary' : ''}`}
                  >
                    <PhotoThumb
                      src={p.url}
                      alt=""
                      className="w-full h-24 object-cover"
                      placeholderClassName="w-full h-24 bg-muted flex items-center justify-center"
                      placeholderIconClassName="h-6 w-6 text-muted-foreground"
                    />
                    {p.moderation_status !== 'approved' && (
                      <span className="absolute top-1 right-1 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-medium text-white">
                        {p.moderation_status === 'rejected' ? 'Rejected' : 'Review'}
                      </span>
                    )}
                    {p.is_primary && (
                      <span className="absolute bottom-1 left-1 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground">
                        Primary
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Moderation Details */}
          <div className="rounded-lg border bg-card p-4">
            <div className="text-sm font-medium text-muted-foreground mb-3">Moderation Checks</div>
            <div className="space-y-3">
              {detail.checks.map((check) => (
                <ModerationCheckCard key={check.log_id} check={check} />
              ))}
            </div>
          </div>

          {/* Decision (only if not yet decided, and not in the refused-photo review) */}
          {!detail.quarantine && detail.checks.some((c) => c.admin_decision === null && c.result === 'fail') && (
            <div className="rounded-lg border bg-card p-4 space-y-4">
              <div className="text-sm font-medium">Decision</div>
              <div>
                <label className="text-sm text-muted-foreground">Notes (optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="mt-1 w-full rounded-lg border bg-background p-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder="Reason for decision..."
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => handleResolve('approve')}
                  disabled={resolving}
                  className="flex items-center gap-2 rounded-lg bg-green-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {resolving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                  Approve (False Positive)
                </button>
                <button
                  onClick={() => handleResolve('reject')}
                  disabled={resolving}
                  className="flex items-center gap-2 rounded-lg bg-red-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {resolving ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                  Confirm Rejection
                </button>
              </div>
            </div>
          )}

          {/* Already resolved */}
          {!detail.quarantine && detail.checks.every((c) => c.admin_decision !== null || c.result !== 'fail') && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <div className="text-sm font-medium text-blue-800">Already Reviewed</div>
              <div className="mt-1 text-sm text-blue-700">
                Decision: {detail.checks.find((c) => c.admin_decision)?.admin_decision ?? 'Auto-approved'}
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function PhotoModerationPage() {
  const { show } = useNavigation();
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('pending_review');
  // "Flagged for" carries a check type and, for photo_subject, one category
  // ("photo_subject:minor_primary") — issue #3608: the child-safety category was
  // reachable only by opening each pending row, and every open is an audited reveal.
  const [flaggedFor, setFlaggedFor] = useState('all');
  const { checkType: checkTypeFilter, category: subjectCategoryFilter } = splitFlaggedFor(flaggedFor);
  const [confidenceMin, setConfidenceMin] = useState('');
  // Only the explicit-content check records a confidence: photos.ts logs a literal 0 for
  // the other two, so a threshold there once emptied the queue with no explanation.
  const confidenceApplies = checkTypeFilter === CONFIDENCE_CHECK_TYPE;
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Repeat refusals of one member, seen together: "N refused" filters the list to them.
  const [memberFilter, setMemberFilter] = useState<{ id: string; name: string } | null>(null);
  // Legal holds (migration 370): super_admin only; the API refuses everyone else too.
  const { hasPermission } = useAdminPermissions();
  const canSeeLegalHolds = hasPermission('moderation.photos.legal_hold');
  const legalHoldsTab = statusFilter === 'legal_holds' && canSeeLegalHolds;
  // The legal-holds tab has its own route; the list keeps its last real filter.
  const queueStatus = statusFilter === 'legal_holds' ? 'pending_review' : statusFilter;
  const { data, isLoading, isError, refetch } = useList<QueueItem>({
    resource: 'moderation/photos',
    pagination: { current: 1, pageSize: 50 },
    filters: [
      { field: 'status', operator: 'eq' as const, value: queueStatus },
      ...(checkTypeFilter !== 'all' ? [{ field: 'check_type', operator: 'eq' as const, value: checkTypeFilter }] : []),
      ...(subjectCategoryFilter !== 'all' ? [{ field: 'subject_category', operator: 'eq' as const, value: subjectCategoryFilter }] : []),
      ...(confidenceMin && confidenceApplies ? [{ field: 'confidence_min', operator: 'eq' as const, value: confidenceMin }] : []),
      ...(memberFilter ? [{ field: 'user_id', operator: 'eq' as const, value: memberFilter.id }] : []),
    ],
    sorters: [{ field: 'created_at', order: sortOrder }],
  });

  const { data: statsData, refetch: refetchStats } = useCustom<ModerationStats>({
    url: `/v1/admin/moderation/stats`,
    method: 'get',
  });

  // The queue stays mounted while the review page shows, so its list and counts are
  // cached: every way back refetches both (a decision there changed them — "Pending 1"
  // stayed after a confirm until a reload, PREDEV 2026-10-19).
  const backToQueue = useCallback(() => {
    setSelectedPhotoId(null);
    void refetch();
    void refetchStats();
  }, [refetch, refetchStats]);

  const queue = data?.data ?? [];
  const stats = statsData?.data;

  if (selectedPhotoId) {
    return (
      <ReviewDetail
        photoId={selectedPhotoId}
        onBack={backToQueue}
        onResolved={backToQueue}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Photo Moderation</h2>
          <p className="text-muted-foreground">
            Review photos flagged by automated content screening
          </p>
          <p className="mt-1 max-w-2xl text-xs text-muted-foreground">
            A photo the screen refuses leaves the member's photos at once and is
            kept, with no time limit, until a reviewer decides it. Open it with
            Review: you see it there (an explicit photo stays blurred until you
            reveal it, and every reveal is logged) and you restore it, confirm
            the refusal or put it on legal hold on the same page.
          </p>
        </div>
        {stats && (
          <div className="flex gap-3">
            <div className="rounded-lg border bg-card px-4 py-2 text-center">
              <div className="text-2xl font-bold text-amber-600">{stats.pending_count}</div>
              <div className="text-xs text-muted-foreground">Pending</div>
            </div>
            <div className="rounded-lg border bg-card px-4 py-2 text-center">
              <div className="text-2xl font-bold text-red-600">{stats.flagged_today}</div>
              <div className="text-xs text-muted-foreground">Flagged Today</div>
            </div>
            <div className="rounded-lg border bg-card px-4 py-2 text-center">
              <div className="text-2xl font-bold text-muted-foreground">{stats.overridden_count}</div>
              <div className="text-xs text-muted-foreground">False Positives</div>
            </div>
          </div>
        )}
      </div>

      {/* Status Filter Tabs */}
      <div className="flex gap-2 border-b">
        {[
          { value: 'pending_review', label: 'Pending Review', icon: Clock },
          { value: 'all', label: 'All', icon: Eye },
          { value: 'rejected', label: 'Rejected', icon: XCircle },
          { value: 'approved', label: 'Overridden', icon: CheckCircle },
          ...(canSeeLegalHolds ? [{ value: 'legal_holds', label: 'Legal holds', icon: Scale }] : []),
        ].map((tab) => (
          <button
            key={tab.value}
            onClick={() => setStatusFilter(tab.value)}
            className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
              statusFilter === tab.value
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:border-muted hover:text-foreground'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {legalHoldsTab ? (
        <LegalHoldsTab />
      ) : isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : isError ? (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-destructive">
          Failed to load moderation queue. Please try again.
        </div>
      ) : (
      <>
      {/* Check-type + confidence filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground">Flagged for:</label>
          <select
            value={flaggedFor}
            onChange={(e) => {
              setFlaggedFor(e.target.value);
              // A threshold kept from the explicit check would sit there looking active
              // while matching nothing: the other two checks record no confidence.
              if (splitFlaggedFor(e.target.value).checkType !== CONFIDENCE_CHECK_TYPE) setConfidenceMin('');
            }}
            aria-label="Filter by check type"
            className="h-9 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="all">All types</option>
            <option value="explicit_content">Explicit content</option>
            <option value="contact_info">Contact info</option>
            <option value="photo_subject">Photo subject — every category</option>
            <optgroup label="Photo subject, one category">
              {PHOTO_SUBJECT_CATEGORIES.filter((c) => c !== 'ok').map((c) => (
                <option key={c} value={`photo_subject:${c}`} title={PHOTO_SUBJECT_CATEGORY_TEXT[c].meaning}>
                  {PHOTO_SUBJECT_CATEGORY_TEXT[c].severe ? '⚠ ' : ''}{PHOTO_SUBJECT_CATEGORY_TEXT[c].label}
                </option>
              ))}
            </optgroup>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label
            className={`text-sm ${confidenceApplies ? 'text-muted-foreground' : 'text-muted-foreground/50'}`}
            title={confidenceApplies ? undefined : CONFIDENCE_ONLY_EXPLICIT}
          >
            Min confidence:
          </label>
          <select
            value={confidenceMin}
            onChange={(e) => setConfidenceMin(e.target.value)}
            disabled={!confidenceApplies}
            aria-label="Filter by minimum confidence"
            title={confidenceApplies ? undefined : CONFIDENCE_ONLY_EXPLICIT}
            className="h-9 rounded-lg border bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="">Any</option>
            <option value="90">≥ 90%</option>
            <option value="75">≥ 75%</option>
            <option value="50">≥ 50%</option>
            <option value="25">≥ 25%</option>
          </select>
          {!confidenceApplies && (
            <span className="text-xs text-muted-foreground">Explicit content only</span>
          )}
        </div>
        {memberFilter && (
          <span className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-900">
            Only {memberFilter.name}'s photos
            <button
              type="button"
              onClick={() => setMemberFilter(null)}
              aria-label="Show every member's photos"
              title="Show every member's photos"
              className="rounded-full p-0.5 hover:bg-amber-200"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        )}
      </div>

      <StatusLegend />

      {/* Photos Table */}
      <div className="rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Photo</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">User</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Flagged For</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
                <SortableHeader
                  field="created_at"
                  label="Date"
                  activeField="created_at"
                  activeOrder={sortOrder}
                  onSort={() => setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'))}
                />
                <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">Action</th>
              </tr>
            </thead>
            <tbody>
              {queue.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <Shield className="h-12 w-12 mx-auto text-green-500 mb-3" />
                    <div className="text-muted-foreground">
                      {statusFilter === 'pending_review' ? 'No photos pending review' : 'No moderation records found'}
                    </div>
                  </td>
                </tr>
              ) : (
                queue.map((item) => {
                  const status = moderationStatus(item);
                  return (
                    <tr key={item.photo_id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-3">
                        <QueueThumb thumbnailUrl={item.thumbnail_url} blurredThumbnail={item.blurred_thumbnail ?? null} decision={item.quarantine?.decision ?? null} />
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => show('users', item.user_id)}
                          className="group text-left"
                        >
                          <div className="font-medium text-primary group-hover:underline">
                            {item.display_name || 'No name'}
                          </div>
                          <div className="text-xs text-muted-foreground">{item.email}</div>
                        </button>
                        {!memberFilter && (
                          <MemberRefusalsChip
                            count={item.member_waiting ?? 0}
                            onPick={() => setMemberFilter({ id: item.user_id, name: item.display_name || item.email })}
                          />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <FlagChips checks={item.checks} />
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={status} />
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {formatDate(item.created_at)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setSelectedPhotoId(item.photo_id)}
                          className="rounded-lg bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                        >
                          Review
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}
    </div>
  );
}
